import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Briefcase, ArrowRight, X } from "@phosphor-icons/react";
import Layout, { PageHeader } from "../components/Layout";
import SEO from "../components/SEO";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { IMAGES } from "../data/images";

function ApplyModal({ job, onClose }) {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email) return toast.error("Name and email are required.");
    setLoading(true);
    try {
      await api.post("/applications", { job_id: job.id, job_title: job.title, ...data });
      toast.success("Application submitted. Thank you!");
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-os-bg/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="apply-modal">
      <div className="bg-white border border-ez-text w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-ez-text px-6 py-4 sticky top-0 bg-ez-bg">
          <div>
            <div className="font-mono-ez text-xs uppercase tracking-widest text-ez-text/50">Apply</div>
            <div className="font-display text-lg uppercase tracking-tight">{job.title}</div>
          </div>
          <button onClick={onClose} data-testid="apply-close" aria-label="Close"><X size={22} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="application-form">
          {[
            ["name", "Full name *"],
            ["email", "Email *"],
            ["phone", "Phone"],
            ["linkedin", "LinkedIn URL"],
            ["resume_url", "Resume link (Drive/URL)"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="block text-[11px] font-mono-ez uppercase tracking-wider text-ez-text/50 mb-1">{label}</label>
              <input data-testid={`app-field-${k}`} value={data[k] || ""} onChange={(e) => set(k, e.target.value)} className="w-full border border-ez-text px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ez-yellow ring-inset" />
            </div>
          ))}
          <div>
            <label className="block text-[11px] font-mono-ez uppercase tracking-wider text-ez-text/50 mb-1">Cover note</label>
            <textarea data-testid="app-field-cover" rows={3} value={data.cover_note || ""} onChange={(e) => set("cover_note", e.target.value)} className="w-full border border-ez-text px-3 py-2.5 text-sm outline-none resize-none focus:ring-2 focus:ring-ez-yellow ring-inset" />
          </div>
          <button type="submit" disabled={loading} data-testid="app-submit" className="w-full bg-ez-text text-ez-bg py-3 font-semibold hover:bg-ez-yellow hover:text-ez-text transition-colors disabled:opacity-50">
            {loading ? "Submitting…" : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO title="Careers" description="Build supply chain execution with Eazymuv. Open roles across operations, product and network." />
      <PageHeader dark eyebrow="Careers" title={<>Build execution with us</>} subtitle="We hire operators and builders who want to run supply chains, not just diagram them." image={IMAGES.frozenStacks} imageAlt="Cold-chain warehouse operations" />
      <section className="max-w-[1600px] mx-auto px-5 md:px-8 py-20 md:py-28">
        {loading ? (
          <div className="text-ez-text/50 font-mono-ez text-sm">Loading roles…</div>
        ) : jobs.length === 0 ? (
          <div className="border border-ez-text p-10 text-center text-ez-text/60" data-testid="no-jobs">No open roles right now. Check back soon.</div>
        ) : (
          <Stagger className="border border-ez-text divide-y divide-ez-text" data-testid="jobs-list">
            {jobs.map((job) => (
              <StaggerItem key={job.id}>
                <div className="p-8 md:p-10 group hover:bg-ez-yellow transition-colors">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                      <div className="flex flex-wrap items-center gap-4 font-mono-ez text-xs uppercase tracking-wider text-ez-text/50 mb-3">
                        <span className="inline-flex items-center gap-1.5"><Briefcase size={14} />{job.department}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin size={14} />{job.location}</span>
                        <span>{job.employment_type}</span>
                      </div>
                      <div className="font-display text-2xl md:text-3xl uppercase tracking-tight leading-tight">{job.title}</div>
                      <p className="mt-3 text-ez-text/70 max-w-2xl">{job.summary}</p>
                    </div>
                    <button onClick={() => setActive(job)} data-testid={`apply-btn-${job.id}`} className="shrink-0 inline-flex items-center gap-2 bg-ez-text text-ez-bg px-6 py-3 font-semibold group-hover:bg-white group-hover:text-ez-text transition-colors">
                      Apply <ArrowRight size={16} weight="bold" />
                    </button>
                  </div>
                  {(job.responsibilities?.length > 0 || job.requirements?.length > 0) && (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                      {job.responsibilities?.length > 0 && (
                        <div>
                          <div className="font-semibold mb-2">Responsibilities</div>
                          <ul className="space-y-1 text-ez-text/70 list-disc pl-5">
                            {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        </div>
                      )}
                      {job.requirements?.length > 0 && (
                        <div>
                          <div className="font-semibold mb-2">Requirements</div>
                          <ul className="space-y-1 text-ez-text/70 list-disc pl-5">
                            {job.requirements.map((r, i) => <li key={i}>{r}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </section>
      {active && <ApplyModal job={active} onClose={() => setActive(null)} />}
    </Layout>
  );
}
